/**
 * Job Category Select Component
 *
 * Searchable dropdown for selecting job categories in dashboard filters
 */

import { Input } from '@/shared/ui/input';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { JobCategory } from '../api/filtersApi';

interface JobCategorySelectProps {
  categories: JobCategory[];
  value: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
}

export default function JobCategorySelect({
  categories,
  value,
  onChange,
  isLoading,
}: JobCategorySelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;

    const searchTerm = search.toLowerCase().trim();
    const searchWords = searchTerm.split(/\s+/);

    return categories.filter((category) => {
      const categoryName = category.name.toLowerCase();
      // All search words must be found in the category name
      return searchWords.every((word) => categoryName.includes(word));
    });
  }, [categories, search]);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === value),
    [categories, value],
  );

  const handleSelect = (categoryId: string) => {
    onChange(categoryId);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative">
      {/* Display selected category or search input */}
      <div
        className="relative cursor-pointer"
        onClick={() => {
          if (value !== 'all') {
            // If a category is selected, clicking should open dropdown
            setIsOpen(!isOpen);
          }
        }}
      >
        {value === 'all' || !selectedCategory ? (
          <div
            className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors bg-orange-600 text-white cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            All Job Types
          </div>
        ) : (
          <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-colors bg-orange-600 text-white">
            <span>{selectedCategory.name}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSelect('all');
              }}
              className="ml-2 hover:bg-orange-700 rounded p-0.5"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />

          {/* Dropdown content */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-[61] max-h-64 flex flex-col">
            {/* Search input */}
            <div className="p-2 border-b border-border sticky top-0 bg-card">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search job types..."
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Category list */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  Loading...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                  No job types found
                </div>
              ) : (
                <>
                  <button
                    onClick={() => handleSelect('all')}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                      value === 'all' ? 'bg-orange-600 text-white' : 'text-foreground'
                    }`}
                  >
                    All Job Types
                  </button>
                  {filteredCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleSelect(String(category.id))}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent ${
                        value === String(category.id)
                          ? 'bg-orange-600 text-white'
                          : 'text-foreground'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
